/**
 * Onglet Charge de la vue d'ensemble (plan F6) : heatmap membres × semaines, tableau
 * membres × projets, compteurs de charge non planifiée et de tâches non estimées.
 * Le niveau de charge est toujours porté par un libellé texte, jamais par la seule couleur.
 */

import { useTranslation } from 'react-i18next';
import { Empty } from '../../components/common.jsx';
import { TD, TH } from '../../ui/theme.js';

const LEVEL_KEYS = {
  available: 'overview.level.available',
  loaded: 'overview.level.loaded',
  overloaded: 'overview.level.overloaded',
};

// t('overview.level.available') t('overview.level.loaded') t('overview.level.overloaded')
const LEVEL_CLASSES = {
  available: 'text-ink-soft',
  loaded: 'bg-statut-alerte-wash text-statut-alerte',
  overloaded: 'bg-statut-erreur-wash text-statut-erreur',
};

function formatHours(locale, value) {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
}

function formatWeekLabel(week) {
  const [, month, day] = week.split('-');
  return `${day}/${month}`;
}

export function OverviewWorkload({ data, memberId }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];

  const weeks = data?.weeks ?? [];
  const members = (data?.members ?? []).filter((member) => !memberId || member.id === memberId);
  const projects = data?.projects ?? [];
  const workload = data?.workload;

  if (!data) return null;
  if (members.length === 0) {
    return <Empty>{t('overview.empty.members')}</Empty>;
  }

  const cellFor = (memberIdValue, week) =>
    workload?.cells.find((cell) => cell.memberId === memberIdValue && cell.week === week);
  const byMemberProjectFor = (memberIdValue, projectId) =>
    workload?.byMemberProject.find(
      (row) => row.memberId === memberIdValue && row.projectId === projectId,
    );
  const byMemberFor = (memberIdValue) =>
    workload?.byMember.find((row) => row.memberId === memberIdValue);
  const byProjectFor = (projectId) =>
    workload?.byProject.find((row) => row.projectId === projectId);
  const grandTotalHours = (workload?.byProject ?? []).reduce((sum, row) => sum + row.hours, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="overflow-x-auto">
        <table className="w-full border-2 border-line">
          <thead>
            <tr>
              <th className={TH}>{t('overview.workload.member')}</th>
              {weeks.map((week) => (
                <th key={week} className={TH}>
                  {formatWeekLabel(week)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <td className={TD}>{member.name}</td>
                {weeks.map((week) => {
                  const cell = cellFor(member.id, week);
                  return (
                    <td key={week} className={`${TD} ${cell ? LEVEL_CLASSES[cell.level] : ''}`}>
                      {cell ? (
                        <>
                          <span className="font-mono tabular-nums">
                            {t('overview.workload.hours', {
                              value: formatHours(locale, cell.hours),
                            })}
                          </span>
                          <span className="block text-[10px] uppercase tracking-label">
                            {t(LEVEL_KEYS[cell.level])}
                          </span>
                        </>
                      ) : (
                        <>
                          <span>{t('overview.workload.noValue')}</span>
                          <span className="block text-[10px] uppercase tracking-label">
                            {t(LEVEL_KEYS.available)}
                          </span>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-2 border-line">
          <thead>
            <tr>
              <th className={TH}>{t('overview.workload.member')}</th>
              {projects.map((project) => (
                <th key={project.id} className={TH}>
                  {project.key}
                </th>
              ))}
              <th className={TH}>{t('overview.workload.total')}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <td className={TD}>{member.name}</td>
                {projects.map((project) => {
                  const row = byMemberProjectFor(member.id, project.id);
                  return (
                    <td key={project.id} className={TD}>
                      {row
                        ? t('overview.workload.hours', { value: formatHours(locale, row.hours) })
                        : t('overview.workload.noValue')}
                    </td>
                  );
                })}
                <td className={TD}>
                  {t('overview.workload.hours', {
                    value: formatHours(locale, byMemberFor(member.id)?.hours ?? 0),
                  })}
                </td>
              </tr>
            ))}
            <tr>
              <td className={TD}>{t('overview.workload.total')}</td>
              {projects.map((project) => (
                <td key={project.id} className={TD}>
                  {t('overview.workload.hours', {
                    value: formatHours(locale, byProjectFor(project.id)?.hours ?? 0),
                  })}
                </td>
              ))}
              <td className={TD}>
                {t('overview.workload.hours', { value: formatHours(locale, grandTotalHours) })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ul className="flex flex-wrap gap-4">
        {members.map((member) => {
          const row = byMemberFor(member.id);
          return (
            <li key={member.id} className="text-sm text-ink-soft">
              <span className="font-semibold text-ink">{member.name}</span>{' '}
              {t('overview.workload.unplannedHours', {
                value: formatHours(locale, row?.unplannedHours ?? 0),
              })}
              {' · '}
              {t('overview.workload.unestimatedTasks', { count: row?.unestimatedTasks ?? 0 })}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
